import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  db,
} from "../firebase.web";

import { Config } from "../config/companies/config";

const CONFIGURATIONS_COLLECTION = "configurations";

export interface ConfigurationDocument {
  id: string;
  name: string;
  key: string;
  config: Config;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  description?: string;
}

export class ConfigService {
  private static instance: ConfigService;

  private constructor() {}

  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  // Get all configurations
  async getAllConfigurations(): Promise<ConfigurationDocument[]> {
    try {
      const q = query(
        collection(db, CONFIGURATIONS_COLLECTION),
        orderBy("createdAt", "desc"),
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as ConfigurationDocument;
      });
    } catch (error) {
      console.error("Error fetching configurations:", error);
      throw error;
    }
  }

  // Get active configurations
  async getActiveConfigurations(): Promise<ConfigurationDocument[]> {
    try {
      const q = query(
        collection(db, CONFIGURATIONS_COLLECTION),
        where("isActive", "==", true),
        orderBy("createdAt", "desc"),
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as ConfigurationDocument;
      });
    } catch (error) {
      console.error("Error fetching active configurations:", error);
      throw error;
    }
  }

  // Get configuration by key
  async getConfigurationByKey(
    key: string,
  ): Promise<ConfigurationDocument | null> {
    try {
      const q = query(
        collection(db, CONFIGURATIONS_COLLECTION),
        where("key", "==", key),
        where("isActive", "==", true),
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot || querySnapshot.docs.length === 0) {
        return null;
      }

      const docSnap = querySnapshot.docs[0];
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as ConfigurationDocument;
    } catch (error) {
      console.error("Error fetching configuration by key:", error);
      throw error;
    }
  }

  // Get configuration by ID
  async getConfigurationById(
    id: string,
  ): Promise<ConfigurationDocument | null> {
    try {
      const docRef = doc(db, CONFIGURATIONS_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data?.createdAt?.toDate() || new Date(),
        updatedAt: data?.updatedAt?.toDate() || new Date(),
      } as ConfigurationDocument;
    } catch (error) {
      console.error("Error fetching configuration by ID:", error);
      throw error;
    }
  }

  // Create new configuration
  async createConfiguration(
    configData: Omit<ConfigurationDocument, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    try {
      const now = new Date();
      const docRef = doc(collection(db, CONFIGURATIONS_COLLECTION));

      await setDoc(docRef, {
        ...configData,
        createdAt: now,
        updatedAt: now,
      });

      return docRef.id;
    } catch (error) {
      console.error("Error creating configuration:", error);
      throw error;
    }
  }

  // Update configuration
  async updateConfiguration(
    id: string,
    updates: Partial<Omit<ConfigurationDocument, "id" | "createdAt">>,
  ): Promise<void> {
    try {
      const docRef = doc(db, CONFIGURATIONS_COLLECTION, id);

      await updateDoc(docRef, {
        ...updates,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error("Error updating configuration:", error);
      throw error;
    }
  }

  // Delete configuration
  async deleteConfiguration(id: string): Promise<void> {
    try {
      const docRef = doc(db, CONFIGURATIONS_COLLECTION, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting configuration:", error);
      throw error;
    }
  }

  // Toggle configuration active status
  async toggleConfigurationStatus(
    id: string,
    isActive: boolean,
  ): Promise<void> {
    try {
      await this.updateConfiguration(id, { isActive: isActive });
    } catch (error) {
      console.error("Error toggling configuration status:", error);
      throw error;
    }
  }

  // Validate configuration
  validateConfiguration(config: Config): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!config.appName) {
      errors.push("App name is required");
    }

    if (!config.mainCollection) {
      errors.push("Main collection is required");
    }

    if (!config.roles || config.roles.length === 0) {
      errors.push("At least one role is required");
    }

    if (!config.screens || config.screens.length === 0) {
      errors.push("At least one screen is required");
    }

    if (!config.database || config.database.length === 0) {
      errors.push("At least one collection is required");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

export const configService = ConfigService.getInstance();
